/**
 * A* pathfinding on a 2D grid.
 * Finds the shortest walkable path between two points,
 * respecting walls and shelf obstacles.
 */

import { isWalkable } from './gridHelpers.js'

/** Cardinal directions: up, right, down, left */
const DIRECTIONS = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
]

/**
 * Finds the shortest path from (startX, startY) to (endX, endY)
 * using A* with Manhattan distance heuristic.
 *
 * @param {string[][]} grid — 2D array of CellType values
 * @param {number} startX
 * @param {number} startY
 * @param {number} endX
 * @param {number} endY
 * @returns {Array<{x: number, y: number}>} — ordered path coordinates, or empty if no path
 */
export function findPath(grid, startX, startY, endX, endY) {
  const height = grid.length
  const width = grid[0]?.length || 0

  if (startX === endX && startY === endY) {
    return [{ x: startX, y: startY }]
  }

  // Min-heap using array (simple priority queue)
  const openSet = []
  const cameFrom = new Map()
  const gScore = new Map()
  const fScore = new Map()

  const key = (x, y) => `${x},${y}`
  const startKey = key(startX, startY)
  const endKey = key(endX, endY)

  gScore.set(startKey, 0)
  fScore.set(startKey, heuristic(startX, startY, endX, endY))
  openSet.push({ x: startX, y: startY, f: fScore.get(startKey) })

  const closedSet = new Set()

  while (openSet.length > 0) {
    // Find node with lowest fScore (simple linear scan — fine for small grids)
    let lowestIdx = 0
    for (let i = 1; i < openSet.length; i++) {
      if (openSet[i].f < openSet[lowestIdx].f) lowestIdx = i
    }
    const current = openSet.splice(lowestIdx, 1)[0]
    const currentKey = key(current.x, current.y)

    if (currentKey === endKey) {
      return reconstructPath(cameFrom, current.x, current.y)
    }

    closedSet.add(currentKey)

    for (const [dx, dy] of DIRECTIONS) {
      const nx = current.x + dx
      const ny = current.y + dy
      const neighborKey = key(nx, ny)

      // Bounds check
      if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue
      // Walkability check
      if (!isWalkable(grid[ny][nx])) continue
      // Already processed
      if (closedSet.has(neighborKey)) continue

      const tentativeG = (gScore.get(currentKey) ?? Infinity) + 1

      if (tentativeG < (gScore.get(neighborKey) ?? Infinity)) {
        cameFrom.set(neighborKey, { x: current.x, y: current.y })
        gScore.set(neighborKey, tentativeG)
        const f = tentativeG + heuristic(nx, ny, endX, endY)
        fScore.set(neighborKey, f)

        // Add to open set if not already there
        if (!openSet.some(n => n.x === nx && n.y === ny)) {
          openSet.push({ x: nx, y: ny, f })
        }
      }
    }
  }

  // No path found
  return []
}

/**
 * Manhattan distance heuristic for A*.
 */
function heuristic(ax, ay, bx, by) {
  return Math.abs(ax - bx) + Math.abs(ay - by)
}

/**
 * Reconstructs the path from the cameFrom map.
 */
function reconstructPath(cameFrom, endX, endY) {
  const path = [{ x: endX, y: endY }]
  let current = `${endX},${endY}`

  while (cameFrom.has(current)) {
    const prev = cameFrom.get(current)
    path.unshift({ x: prev.x, y: prev.y })
    current = `${prev.x},${prev.y}`
  }

  return path
}

/**
 * Calculates the walking distance between two walkable points on the grid.
 * Returns the path length, or Infinity if no path exists.
 */
export function walkingDistance(grid, ax, ay, bx, by) {
  const path = findPath(grid, ax, ay, bx, by)
  return path.length > 0 ? path.length - 1 : Infinity
}
